import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { knowledgeNodes as mockKnowledgeNodes, knowledgeEdges as mockKnowledgeEdges, knowledgeStats as mockKnowledgeStats, typeLabels, typeColors, relationColors } from "@/mocks/knowledgeGraph";
import type { KnowledgeNode, KnowledgeEdge } from "@/mocks/knowledgeGraph";
import { useApi } from "@/hooks/useApi";

// ─── 力导向布局计算 ───
function computeForceLayout(
  nodes: KnowledgeNode[],
  edges: KnowledgeEdge[],
  width: number,
  height: number,
  initialPositions: Map<string, { x: number; y: number }>
): Map<string, { x: number; y: number }> {
  const positions = new Map<string, { x: number; y: number }>();

  nodes.forEach((node, i) => {
    const init = initialPositions.get(node.id);
    if (init) {
      positions.set(node.id, { ...init });
    } else {
      const angle = (i / nodes.length) * Math.PI * 2;
      const radius = Math.min(width, height) * 0.3;
      positions.set(node.id, {
        x: width / 2 + Math.cos(angle) * radius,
        y: height / 2 + Math.sin(angle) * radius,
      });
    }
  });

  for (let step = 0; step < 80; step++) {
    const forces = new Map<string, { fx: number; fy: number }>();
    nodes.forEach((n) => forces.set(n.id, { fx: 0, fy: 0 }));

    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const p1 = positions.get(nodes[i].id)!;
        const p2 = positions.get(nodes[j].id)!;
        const dx = p1.x - p2.x;
        const dy = p1.y - p2.y;
        const dist = Math.sqrt(dx * dx + dy * dy) || 1;
        const force = 16000 / (dist * dist);
        const fx = (dx / dist) * force;
        const fy = (dy / dist) * force;
        const f1 = forces.get(nodes[i].id)!;
        const f2 = forces.get(nodes[j].id)!;
        f1.fx += fx; f1.fy += fy;
        f2.fx -= fx; f2.fy -= fy;
      }
    }

    edges.forEach((edge) => {
      const p1 = positions.get(edge.from)!;
      const p2 = positions.get(edge.to)!;
      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      const dist = Math.sqrt(dx * dx + dy * dy) || 1;
      const targetLength = 110 + (1 - edge.weight) * 70;
      const force = (dist - targetLength) * 0.06;
      const fx = (dx / dist) * force;
      const fy = (dy / dist) * force;
      const f1 = forces.get(edge.from)!;
      const f2 = forces.get(edge.to)!;
      f1.fx += fx; f1.fy += fy;
      f2.fx -= fx; f2.fy -= fy;
    });

    nodes.forEach((node) => {
      const p = positions.get(node.id)!;
      const f = forces.get(node.id)!;
      f.fx += (width / 2 - p.x) * 0.02;
      f.fy += (height / 2 - p.y) * 0.02;
    });

    nodes.forEach((node) => {
      const p = positions.get(node.id)!;
      const f = forces.get(node.id)!;
      p.x += f.fx * 0.4;
      p.y += f.fy * 0.4;
      p.x = Math.max(50, Math.min(width - 50, p.x));
      p.y = Math.max(50, Math.min(height - 50, p.y));
    });
  }

  return positions;
}

// 计算连线与节点边缘的交点
function getEdgePoints(
  p1: { x: number; y: number },
  p2: { x: number; y: number },
  r1: number,
  r2: number
) {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const dist = Math.sqrt(dx * dx + dy * dy) || 1;
  const nx = dx / dist;
  const ny = dy / dist;
  return {
    x1: p1.x + nx * (r1 + 4),
    y1: p1.y + ny * (r1 + 4),
    x2: p2.x - nx * (r2 + 8),
    y2: p2.y - ny * (r2 + 8),
  };
}

const colorMap: Record<string, string> = {
  scene: "#0d9488",
  culture: "#f59e0b",
  history: "#ef4444",
  person: "#3b82f6",
  nature: "#10b981",
  engineering: "#6b7280",
};

function KnowledgeGraphPage() {
  // Live data from the Go API, with bundled mock as fallback while loading /
  // if the backend is unreachable.
  const { data: knowledgeNodes } = useApi<typeof mockKnowledgeNodes>("/data/knowledgeNodes", mockKnowledgeNodes);
  const { data: knowledgeEdges } = useApi<typeof mockKnowledgeEdges>("/data/knowledgeEdges", mockKnowledgeEdges);
  const { data: knowledgeStats } = useApi<typeof mockKnowledgeStats>("/data/knowledgeStats", mockKnowledgeStats);

  const [activeTab, setActiveTab] = useState<"graph" | "nodes" | "edges">("graph");
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const graphRef = useRef<HTMLDivElement>(null);
  const [graphSize, setGraphSize] = useState({ width: 800, height: 400 });

  const [draggingNode, setDraggingNode] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [draggingAny, setDraggingAny] = useState(false);

  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [hoveredEdge, setHoveredEdge] = useState<string | null>(null);

  const [showAiModal, setShowAiModal] = useState(false);
  const [aiExtractText, setAiExtractText] = useState("");
  const [aiExtractType, setAiExtractType] = useState("scene");
  const [aiExtracting, setAiExtracting] = useState(false);
  const [aiExtractResult, setAiExtractResult] = useState<{ nodes: number; edges: number; text: string } | null>(null);

  const [showEdgeModal, setShowEdgeModal] = useState(false);
  const [editingEdge, setEditingEdge] = useState<KnowledgeEdge | null>(null);
  const [edgeForm, setEdgeForm] = useState({ relation: "", weight: 0 });

  const [nodes, setNodes] = useState<KnowledgeNode[]>(knowledgeNodes);
  const [edges, setEdges] = useState<KnowledgeEdge[]>(knowledgeEdges);
  const [introDone, setIntroDone] = useState(false);

  // 动画相关
  const [positions, setPositions] = useState<Map<string, { x: number; y: number }>>(new Map());
  const positionsRef = useRef<Map<string, { x: number; y: number }>>(new Map());
  const animFrameRef = useRef<number>(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    const updateSize = () => {
      if (graphRef.current) {
        const rect = graphRef.current.getBoundingClientRect();
        setGraphSize({ width: rect.width, height: Math.max(420, rect.width * 0.55) });
      }
    };
    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  const filteredNodes = useMemo(() => {
    return nodes.filter((n) => {
      const matchesSearch = searchQuery === "" || n.name.includes(searchQuery) || n.description.includes(searchQuery);
      const matchesType = typeFilter === "all" || n.type === typeFilter;
      return matchesSearch && matchesType;
    });
  }, [searchQuery, typeFilter, nodes]);

  const filteredEdges = useMemo(() => {
    const nodeIds = new Set(filteredNodes.map((n) => n.id));
    return edges.filter((e) => nodeIds.has(e.from) && nodeIds.has(e.to));
  }, [filteredNodes, edges]);

  // 计算与选中节点关联的节点ID
  const relatedNodeIds = useMemo(() => {
    if (!selectedNode) return new Set<string>();
    const related = new Set<string>();
    related.add(selectedNode);
    filteredEdges.forEach((e) => {
      if (e.from === selectedNode) related.add(e.to);
      if (e.to === selectedNode) related.add(e.from);
    });
    return related;
  }, [selectedNode, filteredEdges]);

  // 背景漂浮粒子（固定位置，避免随机值跳动）
  const floatParticles = useMemo(() => {
    return Array.from({ length: 14 }, (_, i) => ({
      id: i,
      cx: [(i * 73) % 100, (i * 137 + 33) % 100, (i * 199 + 67) % 100].map((v) => `${v}%`),
      cy: [(i * 89) % 100, (i * 151 + 47) % 100, (i * 211 + 13) % 100].map((v) => `${v}%`),
      dur: `${14 + i * 2.3}s`,
      r: 1 + (i % 3),
      opacityDur: `${3 + (i % 4)}s`,
    }));
  }, [graphSize.width, graphSize.height]);

  const getTargetPositions = useCallback(() => {
    return computeForceLayout(filteredNodes, filteredEdges, graphSize.width, graphSize.height, new Map());
  }, [filteredNodes, filteredEdges, graphSize.width, graphSize.height]);

  // 初始化 / 筛选变化时直接更新位置
  useEffect(() => {
    if (activeTab !== "graph") return;
    if (graphSize.width === 0) return;
    if (draggingNode) return;
    if (!introDone) return; // 等待入场动画完成

    const target = getTargetPositions();
    positionsRef.current = target;
    setPositions(target);
  }, [activeTab, graphSize.width, graphSize.height, filteredNodes, filteredEdges, draggingNode, introDone, getTargetPositions]);

  // 弹簧动画
  const startSpringAnimation = useCallback((from: Map<string, { x: number; y: number }>, to: Map<string, { x: number; y: number }>) => {
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);

    const vels = new Map<string, { vx: number; vy: number }>();
    Array.from(from.keys()).forEach((id) => vels.set(id, { vx: 0, vy: 0 }));

    setIsAnimating(true);

    const animate = () => {
      const current = positionsRef.current;
      const newPositions = new Map<string, { x: number; y: number }>();
      let totalEnergy = 0;

      Array.from(current.keys()).forEach((id) => {
        const pos = current.get(id)!;
        const targetPos = to.get(id);
        if (!targetPos) return;

        const vel = vels.get(id) || { vx: 0, vy: 0 };

        const dx = targetPos.x - pos.x;
        const dy = targetPos.y - pos.y;

        const springK = 0.065;
        const damping = 0.87;

        const newVx = (vel.vx + dx * springK) * damping;
        const newVy = (vel.vy + dy * springK) * damping;

        const newX = pos.x + newVx;
        const newY = pos.y + newVy;

        vels.set(id, { vx: newVx, vy: newVy });
        newPositions.set(id, { x: newX, y: newY });

        totalEnergy += Math.abs(newVx) + Math.abs(newVy);
      });

      positionsRef.current = newPositions;
      setPositions(newPositions);

      if (totalEnergy > 0.5) {
        animFrameRef.current = requestAnimationFrame(animate);
      } else {
        positionsRef.current = to;
        setPositions(to);
        setIsAnimating(false);
      }
    };

    animFrameRef.current = requestAnimationFrame(animate);
  }, []);

  // 自动布局：从画布边缘飞入
  const handleAutoLayout = useCallback(() => {
    const target = getTargetPositions();
    const from = new Map<string, { x: number; y: number }>();

    filteredNodes.forEach((node, i) => {
      const side = i % 4;
      const x = side === 0 ? -60 : side === 1 ? graphSize.width + 60 : Math.random() * graphSize.width;
      const y = side === 2 ? -60 : side === 3 ? graphSize.height + 60 : Math.random() * graphSize.height;
      from.set(node.id, { x, y });
    });

    positionsRef.current = from;
    setPositions(from);
    startSpringAnimation(from, target);
  }, [filteredNodes, graphSize, getTargetPositions, startSpringAnimation]);

  // 入场动画：节点从中心爆炸式扩散后归位
  useEffect(() => {
    if (activeTab !== "graph" || graphSize.width === 0) return;
    if (introDone) return;

    const target = getTargetPositions();
    const cx = graphSize.width / 2;
    const cy = graphSize.height / 2;

    // 初始聚集在中心
    const start = new Map<string, { x: number; y: number }>();
    filteredNodes.forEach((node) => {
      start.set(node.id, {
        x: cx + (Math.random() - 0.5) * 30,
        y: cy + (Math.random() - 0.5) * 30,
      });
    });
    positionsRef.current = start;
    setPositions(start);

    const startTime = Date.now();
    const duration = 1400;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3); // ease-out cubic
      const spreadPhase = Math.sin(progress * Math.PI) * 0.4; // 向外爆发峰值

      const newPositions = new Map<string, { x: number; y: number }>();
      filteredNodes.forEach((node) => {
        const t = target.get(node.id);
        if (!t) return;
        const angle = Math.atan2(t.y - cy, t.x - cx);
        const maxDist = Math.min(graphSize.width, graphSize.height) * 0.42;
        const dist = maxDist * (1 - eased + spreadPhase);
        const sx = cx + Math.cos(angle) * dist;
        const sy = cy + Math.sin(angle) * dist;
        const x = sx + (t.x - sx) * eased;
        const y = sy + (t.y - sy) * eased;
        newPositions.set(node.id, { x, y });
      });

      positionsRef.current = newPositions;
      setPositions(newPositions);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setIntroDone(true);
      }
    };

    requestAnimationFrame(animate);
  }, [activeTab, graphSize, introDone, filteredNodes, getTargetPositions]);

  // 筛选变化时重置入场动画
  useEffect(() => {
    setIntroDone(false);
  }, [searchQuery, typeFilter, activeTab]);

  const handleMouseDown = useCallback((e: React.MouseEvent, nodeId: string) => {
    e.preventDefault();
    e.stopPropagation();
    const svg = graphRef.current?.querySelector("svg");
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const pos = positionsRef.current.get(nodeId);
    if (!pos) return;
    setDraggingNode(nodeId);
    setDraggingAny(true);
    setDragOffset({
      x: e.clientX - rect.left - pos.x,
      y: e.clientY - rect.top - pos.y,
    });
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      setIsAnimating(false);
    }
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!draggingNode || !graphRef.current) return;
    const svg = graphRef.current.querySelector("svg");
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const newX = Math.max(50, Math.min(graphSize.width - 50, e.clientX - rect.left - dragOffset.x));
    const newY = Math.max(50, Math.min(graphSize.height - 50, e.clientY - rect.top - dragOffset.y));
    positionsRef.current.set(draggingNode, { x: newX, y: newY });
    setPositions(new Map(positionsRef.current));
  }, [draggingNode, dragOffset, graphSize]);

  const handleMouseUp = useCallback(() => {
    if (!draggingNode) return;
    setDraggingNode(null);
    setDraggingAny(false);
    const target = getTargetPositions();
    startSpringAnimation(new Map(positionsRef.current), target);
  }, [draggingNode, getTargetPositions, startSpringAnimation]);

  const handleAiExtract = useCallback(() => {
    if (!aiExtractText.trim()) return;
    setAiExtracting(true);
    setAiExtractResult(null);
    setTimeout(() => {
      const newNodeId = `${nodes.length + 1}`;
      const newNode: KnowledgeNode = {
        id: newNodeId,
        name: aiExtractText.trim().slice(0, 8) + "...",
        type: aiExtractType as KnowledgeNode["type"],
        description: `AI 从文本中提取的知识点: ${aiExtractText.trim().slice(0, 50)}...`,
        relatedCount: 2,
        confidence: 0.82,
        source: "AI 自动提取",
      };
      const newEdge1: KnowledgeEdge = {
        id: `e${edges.length + 1}`,
        from: newNodeId,
        to: "1",
        relation: "AI关联",
        weight: 0.7,
      };
      const newEdge2: KnowledgeEdge = {
        id: `e${edges.length + 2}`,
        from: newNodeId,
        to: "2",
        relation: "AI关联",
        weight: 0.6,
      };
      setNodes((prev) => [...prev, newNode]);
      setEdges((prev) => [...prev, newEdge1, newEdge2]);
      setAiExtractResult({
        nodes: 1,
        edges: 2,
        text: `成功从文本中提取 1 个新节点（${newNode.name}），并建立 2 条关系边。`,
      });
      setAiExtracting(false);
      setAiExtractText("");
    }, 1500);
  }, [aiExtractText, aiExtractType, nodes.length, edges.length]);

  const handleEdgeClick = useCallback((edge: KnowledgeEdge) => {
    setEditingEdge(edge);
    setEdgeForm({ relation: edge.relation, weight: edge.weight });
    setShowEdgeModal(true);
  }, []);

  const handleEdgeSave = useCallback(() => {
    if (!editingEdge) return;
    setEdges((prev) => prev.map((e) => (e.id === editingEdge.id ? { ...e, relation: edgeForm.relation, weight: edgeForm.weight } : e)));
    setShowEdgeModal(false);
    setEditingEdge(null);
  }, [editingEdge, edgeForm]);

  const resetLayout = useCallback(() => {
    const target = getTargetPositions();
    positionsRef.current = target;
    setPositions(target);
  }, [getTargetPositions]);

  const selectedNodeData = selectedNode ? nodes.find((n) => n.id === selectedNode) : null;
  const relatedEdges = selectedNode ? edges.filter((e) => e.from === selectedNode || e.to === selectedNode) : [];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">知识图谱</h1>
          <p className="text-sm text-gray-500 mt-1">三峡知识节点与关系可视化</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleAutoLayout}
            disabled={isAnimating}
            className="px-4 py-2.5 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg text-sm font-medium hover:from-teal-600 hover:to-teal-700 transition-all shadow-sm whitespace-nowrap disabled:opacity-50"
          >
            <i className={`ri-sparkling-line mr-1.5 ${isAnimating ? "animate-spin" : ""}`} />
            {isAnimating ? "弹性归位中..." : "自动布局"}
          </button>
          <button onClick={() => setShowAiModal(true)} className="px-4 py-2.5 bg-gradient-to-r from-violet-500 to-purple-600 text-white rounded-lg text-sm font-medium hover:from-violet-600 hover:to-purple-700 transition-all shadow-sm whitespace-nowrap">
            <i className="ri-magic-line mr-1.5" />AI 自动提取
          </button>
          <button onClick={resetLayout} className="px-4 py-2.5 bg-white text-gray-700 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors whitespace-nowrap">
            <i className="ri-refresh-line mr-1.5" />重置布局
          </button>
          <button className="px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors shadow-sm whitespace-nowrap">
            <i className="ri-add-line mr-1.5" />新增节点
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
        {[
          { label: "知识节点", value: nodes.length, color: "text-gray-900", bg: "bg-white", border: "border-gray-200" },
          { label: "关系边数", value: edges.length, color: "text-teal-700", bg: "bg-teal-50", border: "border-teal-200" },
          { label: "AI自动提取", value: knowledgeStats.autoExtracted, color: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200" },
          { label: "人工审核", value: knowledgeStats.manualReviewed, color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200" },
          { label: "最后更新", value: knowledgeStats.lastUpdated.split(" ")[0], color: "text-gray-700", bg: "bg-gray-50", border: "border-gray-200" },
        ].map((stat) => (
          <div key={stat.label} className={`${stat.bg} border ${stat.border} rounded-lg p-4`}>
            <div className="text-xs text-gray-500 mb-1">{stat.label}</div>
            <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="border-b border-gray-200">
          <div className="flex items-center justify-between px-4 pt-3">
            <div className="flex gap-1">
              {[
                { key: "graph" as const, label: "图谱视图", icon: "ri-node-tree" },
                { key: "nodes" as const, label: "节点列表", icon: "ri-list-check" },
                { key: "edges" as const, label: "关系列表", icon: "ri-links-line" },
              ].map((tab) => (
                <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.key ? "border-teal-600 text-teal-700" : "border-transparent text-gray-500 hover:text-gray-700"}`}>
                  <i className={tab.icon} />
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3 pb-2">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="搜索节点" className="w-48 pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500" />
              </div>
              <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500">
                <option value="all">全部类型</option>
                <option value="scene">景点</option>
                <option value="culture">文化</option>
                <option value="history">历史</option>
                <option value="person">人物</option>
                <option value="nature">自然</option>
                <option value="engineering">工程</option>
              </select>
            </div>
          </div>
        </div>

        <div className="p-4">
          {activeTab === "graph" && (
            <div ref={graphRef} className="relative w-full">
              <svg
                width={graphSize.width}
                height={graphSize.height}
                className={`w-full ${draggingAny ? "cursor-grabbing" : "cursor-grab"}`}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                style={{ overflow: "visible" }}
              >
                <defs>
                  {/* 背景网格 */}
                  <pattern id="dotGrid" width="40" height="40" patternUnits="userSpaceOnUse">
                    <circle cx="20" cy="20" r="1" fill="#d1d5db" opacity="0.5" />
                  </pattern>
                  {/* 箭头 markers */}
                  {Object.entries(relationColors).map(([relation, color]) => (
                    <marker key={relation} id={`arrow-${relation}`} markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                      <polygon points="0 0, 10 3.5, 0 7" fill={color} />
                    </marker>
                  ))}
                  {/* 节点光晕滤镜 */}
                  <filter id="nodeGlow" x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="3" result="blur" />
                    <feComposite in="SourceGraphic" in2="blur" operator="over" />
                  </filter>
                  {/* 连线文字背景 */}
                  <filter id="textBg">
                    <feFlood floodColor="white" floodOpacity="0.9" />
                    <feComposite in2="SourceGraphic" operator="in" />
                  </filter>
                </defs>

                {/* 背景网格 */}
                <rect x="0" y="0" width={graphSize.width} height={graphSize.height} fill="url(#dotGrid)" opacity="0.6" />

                {/* 背景漂浮粒子 */}
                {floatParticles.map((p) => (
                  <circle key={`float-${p.id}`} r={p.r} fill="#94a3b8" opacity="0.3">
                    <animate attributeName="cx" values={p.cx.join(";")} dur={p.dur} repeatCount="indefinite" />
                    <animate attributeName="cy" values={p.cy.join(";")} dur={p.dur} repeatCount="indefinite" />
                    <animate attributeName="opacity" values="0.1;0.4;0.1" dur={p.opacityDur} repeatCount="indefinite" />
                  </circle>
                ))}

                {/* Edges */}
                {filteredEdges.map((edge) => {
                  const p1 = positions.get(edge.from);
                  const p2 = positions.get(edge.to);
                  if (!p1 || !p2) return null;
                  const fromNode = filteredNodes.find((n) => n.id === edge.from);
                  const toNode = filteredNodes.find((n) => n.id === edge.to);
                  if (!fromNode || !toNode) return null;

                  const r1 = 8 + Math.sqrt(fromNode.relatedCount) * 2;
                  const r2 = 8 + Math.sqrt(toNode.relatedCount) * 2;
                  const { x1, y1, x2, y2 } = getEdgePoints(p1, p2, r1, r2);
                  const color = relationColors[edge.relation] || "#94a3b8";
                  const isHovered = hoveredEdge === edge.id;
                  const strokeWidth = (isHovered ? 2.5 : 1) + edge.weight * 2;
                  const strokeOpacity = isHovered ? 0.9 : 0.5;
                  const midX = (x1 + x2) / 2;
                  const midY = (y1 + y2) / 2;
                  const isRelated = !selectedNode || edge.from === selectedNode || edge.to === selectedNode;
                  const dimOpacity = selectedNode ? (isRelated ? 1 : 0.1) : 1;
                  const edgePath = `M ${x1} ${y1} L ${x2} ${y2}`;

                  return (
                    <g key={edge.id} opacity={dimOpacity} style={{ transition: "opacity 0.4s ease" }}>
                      {/* 不可见点击区域 */}
                      <line
                        x1={x1} y1={y1} x2={x2} y2={y2}
                        stroke="transparent"
                        strokeWidth="18"
                        className="cursor-pointer"
                        onMouseEnter={() => setHoveredEdge(edge.id)}
                        onMouseLeave={() => setHoveredEdge(null)}
                        onClick={() => handleEdgeClick(edge)}
                      />
                      {/* 可见连线 */}
                      <line
                        x1={x1} y1={y1} x2={x2} y2={y2}
                        stroke={color}
                        strokeWidth={strokeWidth}
                        strokeOpacity={strokeOpacity * dimOpacity}
                        strokeLinecap="round"
                        markerEnd={`url(#arrow-${edge.relation})`}
                        style={{ transition: "stroke-width 0.2s, stroke-opacity 0.2s" }}
                      />
                      {/* 连线流光 - hover 时 */}
                      {isHovered && (
                        <line
                          x1={x1} y1={y1} x2={x2} y2={y2}
                          stroke={color}
                          strokeWidth={strokeWidth + 3}
                          strokeOpacity={0.3}
                          strokeLinecap="round"
                          strokeDasharray="8 12"
                          style={{ animation: "dashFlow 1s linear infinite" }}
                        >
                          <animate attributeName="stroke-dashoffset" values="0;-20" dur="1s" repeatCount="indefinite" />
                        </line>
                      )}
                      {/* 多重流动粒子 */}
                      {[0, 1].map((particleIndex) => (
                        <circle
                          key={`p-${edge.id}-${particleIndex}`}
                          r={particleIndex === 0 ? 2.5 : 1.5}
                          fill={color}
                          opacity={isHovered ? 0.9 : 0.5}
                        >
                          <animateMotion
                            dur={`${2.5 + (parseInt(edge.id) % 4) * 0.5 + particleIndex * 1.2}s`}
                            repeatCount="indefinite"
                            path={edgePath}
                            begin={`${particleIndex * 0.8}s`}
                          />
                          <animate attributeName="opacity" values="0.2;0.8;0.2" dur={`${2 + particleIndex}s`} repeatCount="indefinite" />
                        </circle>
                      ))}
                      {/* 连线 hover 时两端节点脉冲光圈 */}
                      {isHovered && (
                        <>
                          <circle cx={x1} cy={y1} r={r1} fill="none" stroke={color} strokeWidth="2" opacity="0.6">
                            <animate attributeName="r" values={`${r1};${r1 + 10}`} dur="0.8s" repeatCount="indefinite" />
                            <animate attributeName="opacity" values="0.6;0" dur="0.8s" repeatCount="indefinite" />
                          </circle>
                          <circle cx={x2} cy={y2} r={r2} fill="none" stroke={color} strokeWidth="2" opacity="0.6">
                            <animate attributeName="r" values={`${r2};${r2 + 10}`} dur="0.8s" repeatCount="indefinite" />
                            <animate attributeName="opacity" values="0.6;0" dur="0.8s" repeatCount="indefinite" />
                          </circle>
                        </>
                      )}
                      {/* 关系标签背景 */}
                      <text x={midX} y={midY} fontSize="10" fill="white" textAnchor="middle" dominantBaseline="middle">
                        {edge.relation}
                      </text>
                      <text x={midX} y={midY} fontSize="10" fill="white" textAnchor="middle" dominantBaseline="middle" stroke="white" strokeWidth="6" strokeOpacity="0.9" className="pointer-events-none">
                        {edge.relation}
                      </text>
                      <text x={midX} y={midY} fontSize="10" fill={color} textAnchor="middle" dominantBaseline="middle" fontWeight={isHovered ? "700" : "500"} className="pointer-events-none">
                        {edge.relation}
                      </text>
                    </g>
                  );
                })}

                {/* Nodes */}
                {filteredNodes.map((node) => {
                  const p = positions.get(node.id);
                  if (!p) return null;
                  const isSelected = selectedNode === node.id;
                  const isDragging = draggingNode === node.id;
                  const isHovered = hoveredNode === node.id;
                  const radius = 8 + Math.sqrt(node.relatedCount) * 2;
                  const color = colorMap[node.type] || "#6b7280";
                  const isRelated = !selectedNode || isSelected || relatedNodeIds.has(node.id);
                  const dimOpacity = selectedNode ? (isRelated ? 1 : 0.25) : 1;

                  return (
                    <g key={node.id} opacity={dimOpacity} style={{ transition: "opacity 0.4s ease" }}>
                      {/* 光晕 / 选中环 */}
                      {(isHovered || isSelected) && (
                        <circle
                          cx={p.x} cy={p.y}
                          r={radius + 6}
                          fill="none"
                          stroke={color}
                          strokeOpacity={isSelected ? 0.4 : 0.2}
                          strokeWidth={isSelected ? 2.5 : 2}
                          strokeDasharray={isSelected ? "4 3" : "0"}
                        >
                          {isSelected && (
                            <animateTransform attributeName="transform" type="rotate" from="0" to="360" dur="12s" repeatCount="indefinite" />
                          )}
                        </circle>
                      )}
                      {/* 脉冲光晕 - 选中节点 */}
                      {isSelected && (
                        <circle cx={p.x} cy={p.y} r={radius} fill="none" stroke={color} strokeWidth="3" opacity="0.5">
                          <animate attributeName="r" values={`${radius};${radius + 22}`} dur="2s" repeatCount="indefinite" />
                          <animate attributeName="opacity" values="0.5;0" dur="2s" repeatCount="indefinite" />
                        </circle>
                      )}
                      {/* 节点本体 */}
                      <circle
                        cx={p.x} cy={p.y}
                        r={radius + (isHovered ? 2 : 0)}
                        fill={color}
                        fillOpacity={isSelected ? 1 : 0.88}
                        stroke={isDragging ? "#111827" : isSelected ? "#111827" : isHovered ? color : "#fff"}
                        strokeWidth={isDragging ? 3 : isSelected ? 2.5 : isHovered ? 2 : 1.5}
                        className="cursor-grab"
                        onMouseDown={(e) => handleMouseDown(e, node.id)}
                        onMouseEnter={() => setHoveredNode(node.id)}
                        onMouseLeave={() => setHoveredNode(null)}
                        onClick={() => !isDragging && setSelectedNode(isSelected ? null : node.id)}
                        style={{ filter: isDragging ? "drop-shadow(0 4px 6px rgba(0,0,0,0.2))" : isHovered ? "drop-shadow(0 2px 4px rgba(0,0,0,0.15))" : "none", transition: "r 0.2s" }}
                      >
                        {!isDragging && !isHovered && !isSelected && (
                          <animate attributeName="r" values={`${radius};${radius * 1.05};${radius}`} dur={`${2.5 + (parseInt(node.id) % 3) * 0.5}s`} repeatCount="indefinite" />
                        )}
                      </circle>
                      {/* 节点内部发光点 - 数据脉动 */}
                      <circle cx={p.x} cy={p.y} r={Math.max(2, radius * 0.22)} fill="white" opacity="0.7">
                        <animate attributeName="opacity" values="0.3;0.9;0.3" dur={`${1.8 + (parseInt(node.id) % 4) * 0.4}s`} repeatCount="indefinite" />
                        <animate attributeName="r" values={`${Math.max(2, radius * 0.18)};${Math.max(3, radius * 0.28)};${Math.max(2, radius * 0.18)}`} dur={`${2 + (parseInt(node.id) % 3) * 0.5}s`} repeatCount="indefinite" />
                      </circle>
                      {/* 节点标签 */}
                      <text x={p.x} y={p.y + radius + 14} fontSize="11" fill="#374151" textAnchor="middle" fontWeight={isHovered ? "700" : "500"} className="pointer-events-none" style={{ transition: "font-weight 0.2s" }}>
                        {node.name}
                      </text>
                      {/* 拖拽时坐标显示 */}
                      {isDragging && (
                        <text x={p.x} y={p.y - radius - 8} fontSize="10" fill="#6b7280" textAnchor="middle" className="pointer-events-none">
                          {Math.round(p.x)}, {Math.round(p.y)}
                        </text>
                      )}
                      {/* Hover 时显示类型标签 */}
                      {isHovered && (
                        <text x={p.x} y={p.y + radius + 28} fontSize="9" fill={color} textAnchor="middle" className="pointer-events-none">
                          {typeLabels[node.type]}
                        </text>
                      )}
                    </g>
                  );
                })}
              </svg>

              {/* Drag hint */}
              <div className="absolute bottom-3 left-3 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-lg px-3 py-2 shadow-sm">
                <span className="text-xs text-gray-500">
                  <i className="ri-drag-move-line mr-1" />
                  拖拽节点可自由调整，松开自动弹性归位
                </span>
              </div>

              {/* Legend */}
              <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-lg p-3 shadow-sm">
                <div className="text-xs font-medium text-gray-700 mb-2">节点类型</div>
                <div className="space-y-1.5">
                  {Object.entries(typeLabels).map(([type, label]) => (
                    <div key={type} className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: colorMap[type] }} />
                      <span className="text-xs text-gray-600">{label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Selected Node Info */}
              {selectedNodeData && (
                <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm border border-gray-200 rounded-lg p-4 shadow-sm w-56">
                  <div className="flex items-center justify-between mb-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${typeColors[selectedNodeData.type]}`}>{typeLabels[selectedNodeData.type]}</span>
                    <button onClick={() => setSelectedNode(null)} className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600">
                      <i className="ri-close-line" />
                    </button>
                  </div>
                  <h4 className="text-sm font-bold text-gray-900 mb-1">{selectedNodeData.name}</h4>
                  <p className="text-xs text-gray-500 mb-3">{selectedNodeData.description}</p>
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                    <span>关联数: {selectedNodeData.relatedCount}</span>
                    <span>置信度: {(selectedNodeData.confidence * 100).toFixed(0)}%</span>
                  </div>
                  <div className="text-xs text-gray-400 mb-2">来源: {selectedNodeData.source}</div>
                  {relatedEdges.length > 0 && (
                    <div className="space-y-1 pt-2 border-t border-gray-100">
                      <div className="text-xs font-medium text-gray-700 mb-1">关联关系</div>
                      {relatedEdges.map((edge) => {
                        const otherId = edge.from === selectedNode ? edge.to : edge.from;
                        const otherNode = nodes.find((n) => n.id === otherId);
                        return (
                          <div key={edge.id} className="flex items-center gap-1.5 text-xs cursor-pointer hover:text-teal-600 transition-colors" onClick={() => handleEdgeClick(edge)}>
                            <span className="text-gray-500">{edge.from === selectedNode ? "→" : "←"}</span>
                            <span className="text-teal-600">{edge.relation}</span>
                            <span className="text-gray-700">{otherNode?.name}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === "nodes" && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">节点名称</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">类型</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">描述</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">关联数</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">置信度</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">来源</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredNodes.map((node) => (
                    <tr key={node.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <span className="text-sm font-medium text-gray-900">{node.name}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${typeColors[node.type]}`}>{typeLabels[node.type]}</span>
                      </td>
                      <td className="px-4 py-3 max-w-xs">
                        <span className="text-sm text-gray-600 truncate block">{node.description}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-gray-700">{node.relatedCount}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                            <div className="h-full bg-teal-500 rounded-full" style={{ width: `${node.confidence * 100}%` }} />
                          </div>
                          <span className="text-xs text-gray-600">{(node.confidence * 100).toFixed(0)}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-gray-500">{node.source}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded transition-colors">
                            <i className="ri-edit-line" />
                          </button>
                          <button className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors">
                            <i className="ri-delete-bin-line" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === "edges" && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">关系</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">起始节点</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">目标节点</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">权重</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredEdges.map((edge) => {
                    const fromNode = nodes.find((n) => n.id === edge.from);
                    const toNode = nodes.find((n) => n.id === edge.to);
                    return (
                      <tr key={edge.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3">
                          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">{edge.relation}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm font-medium text-gray-900">{fromNode?.name}</span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm font-medium text-gray-900">{toNode?.name}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div className="h-full bg-teal-500 rounded-full" style={{ width: `${edge.weight * 100}%` }} />
                            </div>
                            <span className="text-xs text-gray-600">{(edge.weight * 100).toFixed(0)}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded transition-colors">
                              <i className="ri-edit-line" />
                            </button>
                            <button className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors">
                              <i className="ri-delete-bin-line" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* AI Extract Modal */}
      {showAiModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowAiModal(false)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg">
                  <i className="ri-magic-line text-white" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">AI 自动提取知识节点</h3>
                  <p className="text-xs text-gray-500">输入文本，AI 自动识别实体并建立关系</p>
                </div>
              </div>
              <button onClick={() => setShowAiModal(false)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">输入文本</label>
                <textarea
                  value={aiExtractText}
                  onChange={(e) => setAiExtractText(e.target.value)}
                  placeholder="粘贴景点介绍、历史文献、游记等内容，AI 将自动提取关键实体和关系..."
                  className="w-full h-32 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500 resize-none"
                />
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-xs text-gray-400">{aiExtractText.length} 字</span>
                  <button onClick={() => setAiExtractText("三峡大坝是长江上最大的水利枢纽，位于西陵峡中段的宜昌市三斗坪镇。大坝由拦河大坝、水电站、通航建筑物等组成。葛洲坝位于三峡大坝下游约38公里处，是长江上第一座大型水利枢纽。")} className="text-xs text-violet-600 hover:text-violet-700">
                    <i className="ri-article-line mr-1" />填充示例
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">优先提取类型</label>
                <div className="flex items-center gap-2 flex-wrap">
                  {[
                    { value: "scene", label: "景点", icon: "ri-map-pin-line" },
                    { value: "person", label: "人物", icon: "ri-user-line" },
                    { value: "history", label: "历史", icon: "ri-history-line" },
                    { value: "culture", label: "文化", icon: "ri-book-line" },
                    { value: "nature", label: "自然", icon: "ri-plant-line" },
                    { value: "engineering", label: "工程", icon: "ri-building-line" },
                  ].map((t) => (
                    <button
                      key={t.value}
                      onClick={() => setAiExtractType(t.value)}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        aiExtractType === t.value
                          ? "bg-violet-100 text-violet-700 border border-violet-200"
                          : "bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100"
                      }`}
                    >
                      <i className={t.icon} />
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              {aiExtractResult && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <i className="ri-checkbox-circle-line text-emerald-600" />
                    <span className="text-sm font-medium text-emerald-800">提取成功</span>
                  </div>
                  <p className="text-sm text-emerald-700">{aiExtractResult.text}</p>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
              <button onClick={() => setShowAiModal(false)} className="px-4 py-2.5 text-gray-600 text-sm font-medium hover:bg-gray-100 rounded-lg transition-colors">
                取消
              </button>
              <button
                onClick={handleAiExtract}
                disabled={aiExtracting || !aiExtractText.trim()}
                className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                  aiExtracting || !aiExtractText.trim()
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-violet-500 to-purple-600 text-white hover:from-violet-600 hover:to-purple-700 shadow-sm"
                }`}
              >
                {aiExtracting ? (
                  <span className="flex items-center gap-2">
                    <i className="ri-loader-4-line animate-spin" />
                    AI 正在分析...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <i className="ri-magic-line" />
                    开始提取
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edge Edit Modal */}
      {showEdgeModal && editingEdge && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowEdgeModal(false)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-teal-100 rounded-lg">
                  <i className="ri-links-line text-teal-600" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">编辑关系</h3>
                  <p className="text-xs text-gray-500">修改关系类型与权重</p>
                </div>
              </div>
              <button onClick={() => setShowEdgeModal(false)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 mb-1">起始节点</div>
                  <div className="text-sm font-medium text-gray-900">{nodes.find((n) => n.id === editingEdge.from)?.name}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 mb-1">目标节点</div>
                  <div className="text-sm font-medium text-gray-900">{nodes.find((n) => n.id === editingEdge.to)?.name}</div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">关系类型</label>
                <input
                  type="text"
                  value={edgeForm.relation}
                  onChange={(e) => setEdgeForm((prev) => ({ ...prev, relation: e.target.value }))}
                  className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                  placeholder="例如：地理位置、文化关联..."
                />
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {["地理位置", "文化关联", "历史事件", "传说相关", "人物关系", "包含设施"].map((preset) => (
                    <button
                      key={preset}
                      onClick={() => setEdgeForm((prev) => ({ ...prev, relation: preset }))}
                      className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-md hover:bg-teal-50 hover:text-teal-700 transition-colors"
                    >
                      {preset}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-medium text-gray-700">关系权重</label>
                  <span className="text-sm font-bold text-teal-700">{(edgeForm.weight * 100).toFixed(0)}%</span>
                </div>
                <input
                  type="range"
                  min="0.1"
                  max="1"
                  step="0.05"
                  value={edgeForm.weight}
                  onChange={(e) => setEdgeForm((prev) => ({ ...prev, weight: parseFloat(e.target.value) }))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-teal-600"
                />
                <div className="flex items-center justify-between text-xs text-gray-400 mt-1">
                  <span>弱</span>
                  <span>强</span>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
              <button onClick={() => setShowEdgeModal(false)} className="px-4 py-2.5 text-gray-600 text-sm font-medium hover:bg-gray-100 rounded-lg transition-colors">
                取消
              </button>
              <button onClick={handleEdgeSave} className="px-4 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors shadow-sm whitespace-nowrap">
                <i className="ri-save-line mr-1.5" />保存关系
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default KnowledgeGraphPage;